import {
	type RedirectMap,
	type Site,
	type TermsApiResponse,
	TermsApiResponseSchema,
	type TermsContent,
} from "@/types";
import { useEffect, useState } from "react";

const cookieExists = (cookieName: string): boolean => {
	return document.cookie.split(";").some((cookie) => {
		return cookie.trim().startsWith(`${cookieName}=`);
	});
};

export default function useTermsAndConditions(
	site: Site,
	redirectMap: RedirectMap,
) {
	const [terms, setTerms] = useState<TermsContent | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const redirectURL = redirectMap[site];

	if (!window.location.host.endsWith("rlam.com")) {
		return { terms: null, loading: false, error: null };
	}

	useEffect(() => {
		async function fetchTerms() {
			try {
				const response = await fetch(
					`${process.env.RLAM_ROOT}/en-gb/api/disclaimercontent`,
				);
				if (!response.ok) {
					throw new Error("Network response was not ok");
				}
				const data = await response.json();

				const parsedResult = TermsApiResponseSchema.safeParse(data);
				if (!parsedResult.success) {
					throw new Error("Invalid API response structure");
				}
				const validData: TermsApiResponse = parsedResult.data;

				const termsItem = validData.result.response.find(
					(item) => item.site === site,
				);
				if (!termsItem) {
					setError("Site not found");
					setLoading(false);
					return;
				}

				const hasPersistCookie = cookieExists(termsItem.persistCookieName);
				const hasSessionCookie = cookieExists(termsItem.sessionCookieName);

				if (!hasPersistCookie && !hasSessionCookie && redirectURL) {
					window.location.href = `${redirectURL}?terms-redirect=${window.location.pathname}`;
					return;
				}

				setTerms(termsItem.contentDto);
				setLoading(false);
			} catch (err) {
				setError(err instanceof Error ? err.message : "An error occurred");
				setLoading(false);
			}
		}

		if (window.location.host.endsWith("rlam.com")) {
			fetchTerms();
		}
	}, [site, redirectURL]);

	return { terms, loading, error };
}
